<template>
    <div>
        <el-table
            class="rounded"
            v-loading="loading"
            :data="data"
            align="center"
            border
        >
            <!-- ID -->
            <el-table-column 
              label="ID" 
              width="80"
              prop="id" 
              align="left"
            >
              <div slot-scope="scope">{{ scope.row.id }}</div>
            </el-table-column>
            
            <!-- Documento protheus -->
            <el-table-column label="Documento" width="150" align="left">
                <template slot-scope="scope">
                    <span>
                        {{ 
                            scope.row.tipo === 'J' 
                                ? formatCNPJ(scope.row.documento)
                                : formatCPF(scope.row.documento)
                        }}
                    </span>
                </template>
            </el-table-column>

            <!-- Tipo -->
            <el-table-column label="Tipo" width="130" align="left">
                <template slot-scope="scope">
                   <span>{{ scope.row.tipo === 'J' ? 'P. Jurídica' : 'P. Física' }}</span>
                </template>
            </el-table-column>

            <!-- Status -->
            <el-table-column label="Status" width="130" align="left">
                <template slot-scope="scope">
                    <el-tag 
                        v-if="scope.row.status"
                        type="info" 
                        size="small"
                        :type="scope.row.status === 'APROVADO' 
                            ? 'success'
                            : 'danger'
                        "
                    >
                        {{ scope.row.status.toUpperCase() }}
                    </el-tag>
                </template>
            </el-table-column>

            <!-- Analisado por -->
            <el-table-column label="Consultado por" min-width="250" align="left">
                <template slot-scope="scope">
                     <div 
                      v-if="scope.row.feito_por" 
                      class="d-flex align-items-center"
                      :style="{ gap: '8px' }"
                     >
                        <el-avatar size="small">
                          {{ scope.row.feito_por.name.charAt(0) }}
                        </el-avatar>

                        <div 
                          class="d-flex flex-column" 
                          :style="{ minWidth: 0, flex: 1 }"
                        >
                          <span 
                            class="font-weight-bold text-truncate"
                            :style="{ fontSize: '14px' }"
                          >
                            {{ scope.row.feito_por.name }}
                          </span>
                        </div>
                      </div>
                      
                      <el-tag 
                        v-else
                        type="info" 
                        size="small"
                      >
                        Não associado
                      </el-tag>
                </template>
            </el-table-column>

            <!-- Criado em -->
            <el-table-column label="Criado em" width="180" align="left">
                <template slot-scope="scope">
                    <div class="d-flex align-items-center">
                        <i class="el-icon-time"></i>
                        <span class="ml-1">{{ formatDate(scope.row.created_at, true) }}</span>
                    </div>
                </template>
            </el-table-column>

            <!-- Acoes -->
            <el-table-column
                v-if="isAuthorized"
                fixed="right"
                align="right"
                width="75"
            >
              <div slot="header" style="width: 100%;">
                  <i class="el-icon-menu" />
              </div>

              <template slot-scope="scope">
                <el-button
                    slot="reference"
                    size="mini"
                    type="primary"
                    plain
                    @click="dispatchSPCConsulta(scope.row.id)"
                    title="Ver detalhes"
                >   
                    <i class="el-icon-right"></i>
                    <i class="el-icon-notebook-2"></i>
                </el-button>
              </template>
            </el-table-column>

            <template slot="empty">
                <el-empty description="Nenhuma consulta disponível"></el-empty>
            </template>
        </el-table>

        <!-- Paginacao -->
        <el-row class="mt-3">
            <el-col>
                <el-pagination
                  background
                  :current-page.sync="pagination.data.currentPage"
                  :page-sizes="pagination.pageSizes"
                  :page-size="pagination.data.perPage"
                  layout="total, sizes, prev, pager, next"
                  :total="pagination.total"
                  @current-change="handlePageChange"
                  @size-change="handleSizeChange"
                />
            </el-col>
        </el-row>

        <!-- SPCShowModal -->
        <SPCShowModal ref="spcConsultaShow" @updated="fetch"/>
    </div>
</template>

<script>
    // Components
    import CardBase from "@/components/_macro/CardBase.vue";
    import { Empty, Table, TableColumn, Notification, Popover } from "element-ui";

    import SPCShowModal from "./SPCShowModal.vue";

    // Api
    import { SPCApi } from "@/api/SPCApi";
    const spcConsultasApi = new SPCApi();

    // Utils
    import { formatDate, formatCNPJ, formatCPF } from "@/util/formatters";

    export default {
        name: "prazo-pagamento-list",
        data() {
            return {
                data: [],
                loading: false,
                filters: {
                    orderBy: 'latest',
                },
                pagination: {
                    data: {
                        currentPage: 1,
                        perPage: 15,
                    },
                    pageSizes: [15, 50, 75, 100],
                    total: 0
                },
            }
        },
        methods: {
            async fetch() {
                try {
                    this.loading = true;

                    const res = await spcConsultasApi.list({
                        ...this.filters,
                        ...this.pagination.data
                    });

                    this.data = res.data;
                    this.pagination.total = res.total;
                } catch (err) {
                    Notification.error({
                        title: 'Erro',
                        message: 'Não foi possível obter os prazos de pagamento!'
                    });
                } finally {
                    this.loading = false;
                }
            },
            async dispatchSPCConsulta(spcConsultaId) {
                this.$refs.spcConsultaShow.spcConsultaId = spcConsultaId;

                const elLoading = this.$loading({ lock: true })

                await this.$refs.spcConsultaShow.show();

                elLoading.close();

                return this.$refs.spcConsultaShow.toggleModal();
            },
            /*
            * Pagination 
            *
            */
            handlePageChange(page) {
                this.pagination.data.currentPage = page;
                this.fetch();
            },
            handleSizeChange(val) {
                this.pagination.data.perPage = val;
                this.pagination.data.currentPage = 1;
                this.fetch();
            },
            formatDate,
            formatCNPJ,
            formatCPF
        },
        computed: {
            isAuthorized() {
                return this.$store.getters['user/hasAnyPermission'](['detail_spc']);
            }
        },
        watch: {
            "filters": {
                handler(_newValue) {
                    this.fetch(_newValue);
                },
                deep: true,
            }
        },
        mounted() {
            this.fetch();
        },
        components: {
            CardBase,
            SPCShowModal,
            [Empty.name]: Empty,
            [Table.name]: Table,
            [TableColumn.name]: TableColumn,
            [Notification.name]: Notification
        },
    }
</script>



// WEBPACK FOOTER //
// resources/assets/js/components/_macro/spc/SPCList.vue